import type { AxiosError } from "axios";

import { REQUEST_STATUSES } from "../constants";
import { api } from "./instance";
import { getErrorResponse } from "../utils/helper";

export class ContactService {
  static async getContacts(
    {
      propertyId,
      organizationId,
      query,
    }: {
      propertyId?: number;
      organizationId?: number;
      query: {
        offset?: number;
        limit?: number;
        sortField?: string;
        sortOrientation?: string;
        filterFieldName?: string;
        filterFieldValue?: string;
        searchText?: string;
      };
    } = { propertyId: undefined, organizationId: undefined, query: {} },
  ) {
    try {
      const url = `/api/v2/contacts`;
      const params = {
        ...query,
        ...(propertyId ? { propertyId } : {}),
        ...(organizationId ? { organizationId } : {}),
      };
      const resp = await api({
        method: "get",
        url,
        params,
      });

      return {
        resp: resp.data,
        err: null,
        status: REQUEST_STATUSES.SUCCEEDED,
      };
    } catch (err) {
      return {
        resp: { data: [], count: 0 },
        err: getErrorResponse(err as AxiosError) as unknown,
        status: REQUEST_STATUSES.FAILED,
      };
    }
  }

  static async getContact({
    propertyId,
    organizationId,
    contactId,
  }: {
    propertyId?: number;
    organizationId?: number;
    contactId: number;
  }) {
    try {
      const url = `/api/v2/contacts/${contactId}`;
      const params = {
        ...(propertyId ? { propertyId } : {}),
        ...(organizationId ? { organizationId } : {}),
      };
      const resp = await api({
        method: "get",
        url,
        params,
      });

      return {
        resp: resp.data,
        err: null,
        status: REQUEST_STATUSES.SUCCEEDED,
      };
    } catch (err) {
      return {
        resp: null,
        err: getErrorResponse(err as AxiosError) as unknown,
        status: REQUEST_STATUSES.FAILED,
      };
    }
  }

  static async addContact({
    propertyId,
    organizationId,
    data,
  }: {
    propertyId?: number;
    organizationId?: number;
    data: {
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
      status?: string;
      type?: string;
      organizationId?: number;
    };
  }) {
    try {
      const params = {
        ...(propertyId ? { propertyId } : {}),
        ...(organizationId ? { organizationId } : {}),
      };
      const resp = await api({
        method: "post",
        url: `/api/v2/contacts`,
        data,
        params,
      });

      return {
        resp: resp.data,
        err: null,
        status: REQUEST_STATUSES.SUCCEEDED,
      };
    } catch (err) {
      return {
        resp: null,
        err: getErrorResponse(err as AxiosError) as unknown,
        status: REQUEST_STATUSES.FAILED,
      };
    }
  }

  static async updateContact({
    propertyId,
    organizationId,
    contactId,
    data,
  }: {
    propertyId?: number;
    organizationId?: number;
    contactId: number;
    data: {
      firstName?: string;
      lastName?: string;
      email?: string;
      phone?: string;
      status?: number;
      type?: string;
    };
  }) {
    try {
      const params = {
        ...(propertyId ? { propertyId } : {}),
        ...(organizationId ? { organizationId } : {}),
      };

      const resp = await api({
        method: "put",
        url: `/api/v2/contacts/${contactId}`,
        params,
        data,
      });

      return {
        resp: resp.data,
        err: null,
        status: REQUEST_STATUSES.SUCCEEDED,
      };
    } catch (err) {
      return {
        resp: null,
        err: getErrorResponse(err as AxiosError) as unknown,
        status: REQUEST_STATUSES.FAILED,
      };
    }
  }

  static async deleteContact({
    propertyId,
    organizationId,
    contactId,
  }: {
    propertyId?: number;
    organizationId?: number;
    contactId: number;
  }) {
    try {
      const params = {
        ...(propertyId ? { propertyId } : {}),
        ...(organizationId ? { organizationId } : {}),
      };
      const resp = await api({
        method: "delete",
        url: `/api/v2/contacts/${contactId}`,
        params,
      });

      return {
        resp: resp.data,
        err: null,
        status: REQUEST_STATUSES.SUCCEEDED,
      };
    } catch (err) {
      return {
        resp: null,
        err: getErrorResponse(err as AxiosError) as unknown,
        status: REQUEST_STATUSES.FAILED,
      };
    }
  }
}
